import { z } from "zod";
import { idSchema, instantSchema } from "@amber/contracts";
import { ITEM_REFERENCE_KINDS } from "./items.js";

const xMediaSchema = z.object({
  kind: z.enum(["video", "animated_gif", "photo"]),
  url: z.string(),
  duration_ms: z.number().nullable(),
  sha256: z.string().nullable(),
  bytes: z.number().nullable(),
  archived: z.boolean(),
  note: z.string().nullable(),
});
export type XMedia = z.infer<typeof xMediaSchema>;

const xQuoteSchema = z.object({ post_id: z.string(), url: z.string(), author_handle: z.string(), text: z.string() });

export const TEXT_BASES = ["page_capture", "supplier_text"] as const;

// The post the item's first post answers, kept as context beside the item and never as the author's words.
const xReplyParentSchema = z.object({
  post_id: z.string(),
  url: z.string(),
  author: z.object({ id: z.string(), handle: z.string(), name: z.string() }).nullable(),
  created_at: z.string().nullable(),
  text: z.string().nullable(),
  note: z.string().nullable(),
});
export type XReplyParent = z.infer<typeof xReplyParentSchema>;

export const xThreadSchema = z.object({
  schema: z.literal("amber.x-thread/1"),
  supplier: z.string(),
  adapter: z.string(),
  observed_at: z.string(),
  root_post_id: z.string(),
  author: z.object({ id: z.string(), handle: z.string(), name: z.string() }),
  posts: z.array(
    z.object({
      post_id: z.string(),
      url: z.string(),
      created_at: z.string(),
      text: z.string(),
      lang: z.string().nullable(),
      in_reply_to: z.string().nullable(),
      quotes: xQuoteSchema.nullable(),
      media: z.array(xMediaSchema),
    }),
  ),
  replying_to: xReplyParentSchema.nullable().default(null),
  thread_complete: z.boolean(),
  text_basis: z.enum(TEXT_BASES),
  page_capture: z.object({ state: z.enum(["captured", "failed"]), route: z.string().nullable(), detail: z.string() }),
  supplier_responses: z.array(
    z.object({ call: z.string(), sha256: z.string(), status: z.number(), observed_at: z.string() }),
  ),
});
export type XThread = z.infer<typeof xThreadSchema>;

export const TEXT_BASIS_LABELS: Record<(typeof TEXT_BASES)[number], string> = {
  page_capture: "Post page captured",
  supplier_text: "Supplier text · the post page could not be captured",
};

export const xThreadViewSchema = z.strictObject({
  author: z.strictObject({ id: z.string(), handle: z.string(), name: z.string() }),
  posts: z.array(
    z.strictObject({
      post_id: z.string(),
      url: z.string(),
      created_at: instantSchema,
      text: z.string(),
      in_reply_to: z.string().nullable(),
      quotes: xQuoteSchema
        .extend({ item_id: idSchema("itm").nullable() })
        .strict()
        .nullable(),
      media: z.array(
        z.strictObject({
          kind: z.enum(["video", "animated_gif", "photo"]),
          url: z.string(),
          archived: z.boolean(),
          artifact_id: idSchema("art").nullable(),
          duration_ms: z.number().nullable(),
          note: z.string().nullable(),
        }),
      ),
    }),
  ),
  replying_to: z
    .strictObject({
      post_id: z.string(),
      url: z.string(),
      author: z.strictObject({ id: z.string(), handle: z.string(), name: z.string() }).nullable(),
      created_at: instantSchema.nullable(),
      text: z.string().nullable(),
      note: z.string().nullable(),
    })
    .nullable(),
  thread_complete: z.boolean(),
  text_basis: z.enum(TEXT_BASES),
  text_basis_label: z.string(),
  page_capture: z.strictObject({
    state: z.enum(["captured", "failed"]),
    route: z.string().nullable(),
    detail: z.string(),
  }),
  supplier: z.string(),
  observed_at: instantSchema,
  supplier_response_ids: z.array(idSchema("art")),
});
export type XThreadView = z.infer<typeof xThreadViewSchema>;

export const itemReferenceViewSchema = z.strictObject({
  kind: z.enum(ITEM_REFERENCE_KINDS),
  label: z.string(),
  item_id: idSchema("itm"),
  title: z.string(),
  url: z.string(),
  status: z.string().nullable(),
});
export type ItemReferenceView = z.infer<typeof itemReferenceViewSchema>;

export const ITEM_FORMS = ["standalone", "excerpt", "preview", "clip", "secondary_report"] as const;
export type ItemForm = (typeof ITEM_FORMS)[number];
export const itemFormSchema = z.enum(ITEM_FORMS);
export const ITEM_FORM_LABELS: Record<ItemForm, string> = {
  standalone: "Standalone",
  excerpt: "Excerpt · quotes another post, context partial",
  preview: "Preview · not the full edition, context partial",
  clip: "Clip · an excerpt of a longer edition, context partial",
  secondary_report: "Secondary report · the reporter's wording, with attributed quotations kept apart",
};
